import {
  Action,
  ConsentCookies,
  CookieMethod,
  ErrorCodes,
  OperationType,
  Position,
  SignType,
  ThemeLoad,
  WarningType,
  WindowType
} from "puzzle";
import { DrawerWindow } from "../components/Drawer/DrawerWindow";

export const getChildLocalization: Action = [{
  moduleName: 'App',
  name: 'getI18nHash',
  mappingName: 'i18nHash',
  type: OperationType.addOperationData
}, {
  data: {
    template: {
      bind: {
        childJsonLocale: '{locale} || "en-US"'
      }
    }
  },
  type: OperationType.data
}, {
  data: {
    template: {
      bind: {
        url: '"/locales/child/" + {childJsonLocale} + ".json?dc="+{i18nHash}',
      }
    },
    isRest: false,
    method: 'get'
  },
  handleError: true,
  mappingName: 'childJson',
  type: OperationType.request
}, {
  moduleName: 'App',
  name: 'setChildJsonLocalization',
  args: ['childJsonLocale', 'childJson']
}];

export const initializeApp: Action = [{
  moduleName: 'App',
  type: OperationType.maskModule,
  waitForModule: true
}, {
  data: {
    method: 'get',
    url: '/initializeUser'
  },
  mappingName: 'initializeUserDataResponse',
  persistent: true,
  handleError: true,
  type: OperationType.request
}, {
  data: {
    template: {
      bind: {
        initializeUserData: '{initializeUserDataResponse.data}',
        url: '{initializeUserDataResponse.data.consentUrl}'
      }
    }
  },
  persistent: ['initializeUserData'],
  type: OperationType.addPersistentData
}, {
  data: {
    template: {
      bind: {
        instanceName: `_.get({initializeUserDataResponse}, 'data.serverName')`
      }
    }
  },
  moduleName: 'App',
  args: ['instanceName'],
  name: 'setInitialDocumentTitle'
}, {
  data: {
    template: {
      bind: {
        policies: `_.get({initializeUserData}, 'systemPolicies')`
      }
    }
  },
  args: ['policies'],
  moduleName: 'Policies',
  name: 'setup'
}, {
  args: ['url'],
  method: CookieMethod.consentUrl,
  type: OperationType.cookie
}, {
  condition: '{initializeUserDataResponse.status} === 429', //429 - TOO_MANY_REQUESTS - for purpose of rate limit feature
  conditionOperations: [{
    moduleName: 'App',
    name: 'getRateLimitReset',
    mappingName: 'rateLimitReset',
    args: ['initializeUserDataResponse'],
    type: OperationType.addOperationData
  }, {
    placeholders: {
      template: {
        bind: {
          seconds: '{rateLimitReset}'
        }
      }
    },
    className: 'sm',
    warningType: WarningType.error,
    text: 'Error.TOO_MANY_REQUESTS.text',
    title: 'Error.TOO_MANY_REQUESTS.title',
    items: [{
      btnName: 'ESCAPE',
      operations: [{
        subType: SignType.request,
        type: OperationType.signOut
      }]
    }, {
      btnName: 'Error.TOO_MANY_REQUESTS.close',
      operations: [{
        subType: SignType.request,
        type: OperationType.signOut
      }]
    }],
    type: OperationType.warning,
  }],
  elseOperations: [{
    condition: '{initializeUserData.userProfile}',
    conditionOperations: [{
      data: {
        template: {
          bind: {
            locale: `_.get({initializeUserData}, 'preferences.language.locale')`
          }
        }
      },
      type: OperationType.data
    }, {
      condition: '{locale}',
      conditionOperations: [{
        data: {
          template: {
            bind: {
              locale: '{locale}'
            }
          }
        },
        change: true,
        type: OperationType.language
      }],
      type: OperationType.conditional
    }, {
      name: 'getChildLocalization',
      type: OperationType.action
    }, {
      args: ['locale'],
      moduleName: 'App',
      name: 'setupDayjsLocale'
    }, {
      args: ['initializeUserData'],
      moduleName: 'App',
      name: 'setInitializeUserData'
    }, {
      data: {
        template: {
          bind: {
            userName: `_.get({initializeUserData}, 'userProfile.userInfo.userName')`,
            preferences: `_.get({initializeUserData}, 'preferences')`
          }
        }
      },
      args: ['userName', 'preferences', 'themes'],
      moduleName: 'Header',
      name: 'setUserData'
    }, {
      moduleName: 'Viewport',
      name: 'announcementRibbonDisplayed',
      mappingName: 'isAnnouncementRibbonDisplayed',
      type: OperationType.addOperationData
    }, {
      condition: `{isAnnouncementRibbonDisplayed} !== true`,
      conditionOperations: [{
        name: 'getAnnouncements',
        type: OperationType.action
      }],
      type: OperationType.conditional
    }, {
      name: 'setAnnouncementData',
      args: ['announcementData'],
      moduleName: 'Viewport',
      waitForModule: true
    }, {
      persistent: ['themes'],
      type: OperationType.removePersistentData
    }, {
      name: 'shouldThemeReload',
      moduleName: 'Header',
      args: ['preferences'],
      type: OperationType.addOperationData
    }, {
      condition: '{shouldThemeReload}',
      conditionOperations: [{
        data: {
          template: {
            bind: {
              theme: `_.get({initializeUserData}, 'preferences.theme')`
            }
          }
        },
        type: OperationType.data
      }, {
        condition: '!{theme}',
        conditionOperations: [{
          data: {
            template: {
              bind: {
                theme: `{themes["primavera-default"].name}`
              }
            }
          },
          type: OperationType.data
        }],
        type: OperationType.conditional
      }, {
        load: ThemeLoad.change,
        redirect: true,
        type: OperationType.theme
      }],
      type: OperationType.conditional
    }, {
      args: ['initializeUserData'],
      moduleName: 'Footer',
      name: 'setLastLoginData'
    }, {
      name: 'getHelpLinksData',
      type: OperationType.action
    }, {
      name: 'getAndPreservePreferenceData',
      type: OperationType.action
    }, {
      name: 'reloadPathPD',
      type: OperationType.action
    }],
    elseOperations: [{
      windowType: WindowType.warning,
      type: OperationType.closeWindow
    }, {
      condition: '{initializeUserData.supportEmail}',
      conditionOperations: [{
        placeholders: {
          template: {
            bind: {
              sendMailTo: '{initializeUserData.supportEmail}'
            }
          }
        },
        className: 'sm',
        warningType: WarningType.error,
        text: 'invalidCredentials.text',
        title: 'invalidCredentials.title',
        type: OperationType.warning,
        closable: false,
        items: [{
          operations: [{
            subType: SignType.request,
            type: OperationType.signOut
          }],
          btnName: 'invalidCredentials.ok',
          btnConfig: {
            fillMode: 'flat',
            pos: Position.end
          }
        }]
      }],
      elseOperations: [{
        className: 'sm',
        warningType: WarningType.error,
        text: 'invalidCredentials.textWithoutMail',
        title: 'invalidCredentials.title',
        type: OperationType.warning,
        closable: false,
        items: [{
          operations: [{
            subType: SignType.request,
            type: OperationType.signOut
          }],
          btnName: 'invalidCredentials.ok',
          btnConfig: {
            fillMode: 'flat',
            pos: Position.end
          }
        }]
      }],
      type: OperationType.conditional
    }],
    type: OperationType.conditional
  }],
  type: OperationType.conditional
}, {
  data: {
    template: {
      drawerState: {
        detached: false
      }
    }
  },
  persistent: ['drawerState'],
  type: OperationType.addPersistentData //important - don't remove this param from dispatcher data
}, {
  name: 'getPreferencesLanguagesAndData',
  type: OperationType.action
}, {
  moduleName: 'ChangeLanguage',
  name: 'setLanguages',
  args: ['languages', 'locale']
}, {
  moduleName: 'App',
  type: OperationType.unmaskModule
}];

export const getAndPreservePreferenceData: Action = [{
  data: {
    method: 'get',
    url: '/preferences'
  },
  handleErrorStatuses: [ErrorCodes.FORBIDDEN, ErrorCodes.NOT_FOUND, ErrorCodes.BAD_REQUEST, ErrorCodes.INTERNAL_SERVER_ERROR],
  mappingName: 'preferencesDataResponse',
  type: OperationType.request
}, {
  data: {
    template: {
      bind: {
        responseData: '{preferencesDataResponse}',
        preferencesData: '{preferencesDataResponse}'
      }
    }
  },
  name: 'handleRequestException',
  type: OperationType.action
}, {
  data: {
    template: {
      bind: {
        viewType: '{responseData.viewType}'
      }
    }
  },
  moduleName: 'ViewProvider',
  name: 'setViewType',
  args: ['viewType']
}, {
  persistent: ['preferencesData'],
  type: OperationType.addPersistentData
}];

//Added for test purposes only
export const addInitializeUserData: Action = [{
  persistent: ['initializeUserData'],
  type: OperationType.addPersistentData
}];

export const reloadPathPD: Action = [{
  args: ['initializeUserData'],
  moduleName: 'App',
  name: 'checkCookieParams',
  mappingName: 'cookieParams',
  type: OperationType.addOperationData
}, {
  condition: `{firstLoadApp} && _.get({cookieParams}, 'submissionId')`,
  conditionOperations: [{
    persistent: ['cookieParams'],
    type: OperationType.addPersistentData
  }, {
    data: {
      template: {
        bind: {
          path: '{cookieParams.userType} === "VENDOR" ? "taskList" : "submissionList"',
        },
        mainGridFilter: {
          bind: {
            submissionId: '{cookieParams.submissionId}'
          }
        }
      }
    },
    type: OperationType.data
  }, {
    persistent: ['mainGridFilter'],
    type: OperationType.addPersistentData
  }],
  type: OperationType.conditional
}, {
  condition: '{path} !== undefined',
  conditionOperations: [{
    data: {
      template: {
        bind: {
          path: '{path}'
        }
      }
    },
    type: OperationType.redirect
  }],
  type: OperationType.conditional
}, {
  name: 'reloadRouteData',
  type: OperationType.action
}];

export const reloadRouteData: Action = [{
  moduleName: 'App',
  name: 'getPreviousRoute',
  mappingName: 'previousRoute',
  type: OperationType.addOperationData
}, {
  condition: '{firstLoadApp} && (_.get({initializeUserData}, "userProfile.userInfo.userType") === "VENDOR")', //Default route for supplier user
  conditionOperations: [{
    condition: '{route()} || ({previousRoute}  && {previousRoute.replace("/", "")})',
    "view/TaskList": [{
      name: 'reloadSubmissionList',
      type: OperationType.action
    }],
    "view/preferences": [{
      name: 'loadPreferencesData',
      type: OperationType.action
    }],
    defaultOperations: [{
      data: {
        template: {
          currentModuleName: 'TaskList',
          path: '/view/taskList'
        }
      },
      type: OperationType.redirect
    }, {
      name: 'reloadSubmissionList',
      type: OperationType.action
    }],
    type: OperationType.switch
  }],
  elseOperations: [{
    condition: '{route()} || ({previousRoute}  && {previousRoute.replace("/", "")})',
    "view/submission": [{
      name: 'reloadCreateSubmission',
      type: OperationType.action
    }],
    "view/submissionList": [{
      data: {
        template: {
          currentModuleName: 'SubmissionsList'
        }
      },
      name: 'reloadSubmissionList',
      type: OperationType.action
    }],
    "view/dashboard": [{
      name: 'reloadDashboard',
      type: OperationType.action
    }],
    "view/templates": [{
      name: 'reloadTemplates',
      type: OperationType.action
    }],
    "view/preferences": [{
      name: 'loadPreferencesData',
      type: OperationType.action
    }],
    "view/taskList": [{
      data: {
        template: {
          currentModuleName: 'TaskList'
        }
      },
      name: 'reloadSubmissionList',
      type: OperationType.action
    }],
    type: OperationType.switch
  }],
  type: OperationType.conditional
}];

export const getHelpLinksData: Action = [{
  data: {
    isRest: false,
    method: 'get',
    url: '/static/data/config.json' //if config.json file is changed we have to clear browser cache in order to get changed file
  },
  mappingName: 'configData',
  type: OperationType.request
}, {
  persistent: ['configData'],
  type: OperationType.addPersistentData
}];

export const userLogout: Action = [{
  condition: '_.includes(["view/dashboard","view/submissionList","view/taskList"],{route()})',
  conditionOperations: [{
    moduleName: 'MainGrid',
    name: 'updateFolderViewConfiguration'
  }],
  type: OperationType.conditional
}, {
  name: 'removeAnnouncementCache',
  moduleName: 'Viewport'
}, {
  subType: SignType.init,
  type: OperationType.signOut
}];

export const openHelpCenter: Action = [{
  data: {
    template: {
      bind: {
        initializeUserData: '{initializeUserData}',
        configData: '{configData}'
      }
    }
  },
  args: ['initializeUserData', 'configData'],
  moduleName: 'Header',
  name: 'openHelpPage'
}];

export const openGLHome: Action = [{
  data: {
    template: {
      bind: {
        initializeUserData: '{initializeUserData}',
      }
    }
  },
  args: ['initializeUserData'],
  moduleName: 'Header',
  name: 'openGLHome'
}];

export const goToPath: Action = [{
  type: OperationType.redirect
}];

export const goToConsentPage: Action = [{
  consent: true,
  type: OperationType.redirect
}];

export const goToSignIn: Action = [{
  signIn: true,
  type: OperationType.redirect
}];

export const redirectToPreferences: Action = [{
  data: {
    path: '/view/preferences'
  },
  type: OperationType.data
}, {
  condition: '_.includes(["view/submission","view/template","view/submission/","view/template/"],{route()})',
  conditionOperations: [{
    name: 'checkForChangesBeforeRedirect',
    type: OperationType.action
  }],
  elseOperations: [{
    condition: '_.includes(["view/submissionList","view/submissionList/","view/submissionList"],{route()})',
    conditionOperations: [{
      name: 'checkForDrawerChangesBeforeRedirect',
      type: OperationType.action
    }],
    elseOperations: [{
      type: OperationType.redirect
    }],
    type: OperationType.conditional
  }],
  type: OperationType.conditional
}];

export const openDrawerPopupWindow: Action = [{
  persistent: ['currentModuleName'],
  type: OperationType.addPersistentData
}, {
  name: 'updateDrawerDetachedState',
  type: OperationType.action
}, {
  children: [{
    render: DrawerWindow,
    propArgs: [
      'selectedRecord',
      'initializeUserData',
      'quotesInView',
      'tabsMap',
      'preselectedDrawerTab'
    ]
  }],
  pos: 'previous-screen',
  moduleName: 'App',
  onCloseOperations: [{
    evaluate: true,
    moduleName: '{currentModuleName}',
    name: 'newWindowUnloaded'
  }, {
    name: 'updateDrawerState',
    type: OperationType.action
  }, {
    data: {
      template: {
        detached: false
      }
    },
    name: 'updateDrawerDetachedState',
    type: OperationType.action
  }, {
    persistent: ['currentModuleName'],
    type: OperationType.removePersistentData
  }],
  placeholders: {
    template: {
      bind: {
        subData: "{selectedRecord.submissionId} + ' ' + {selectedRecord.submissionName}"
      }
    }
  },
  title: 'drawer.title',
  windowId: '',
  type: OperationType.popupWindow
}];

export const getAnnouncements: Action = [{
  moduleName: 'App',
  type: OperationType.maskModule
},
  {
    data: {
      method: 'get',
      url: '/announcement'
    },
    mappingName: 'announcementData',
    type: OperationType.request
  }]

export const removeAnnouncementRibbon: Action = [{
  name: 'removeAnnouncementRibbon',
  moduleName: 'AnnouncementRibbon'
}, {
  moduleName: 'App',
  type: OperationType.unmaskModule
}]

export const switchTheme: Action = [{
  moduleName: 'App',
  type: OperationType.maskModule,
}, {
  moduleName: 'CookieManager',
  name: 'hasFunctionalCookie',
  type: OperationType.addOperationData
}, {
  condition: '{hasFunctionalCookie}',
  conditionOperations: [{
    data: {
      template: {
        bind: {
          name: '{themeCookieName}',
          value: '{theme}'
        }
      }
    },
    args: ['name', 'value'],
    cookieType: ConsentCookies.functional,
    method: CookieMethod.setCookie,
    type: OperationType.cookie
  }, {
    load: ThemeLoad.change,
    type: OperationType.theme
  }],
  elseOperations: [{
    load: ThemeLoad.change,
    type: OperationType.theme
  }],
  type: OperationType.conditional
}, {
  condition: '{route() === "view/dashboard"}',
  conditionOperations: [{
    moduleName: 'MainGrid',
    name: 'getGridConfigJsonData',
    mappingName: 'params',
    type: OperationType.addOperationData
  }, {
    name: 'folderViewUpdate',
    type: OperationType.action
  }],
  type: OperationType.conditional
}, {
  name: 'reloadRouteData',
  type: OperationType.action
}, {
  condition: '{hasFunctionalCookie}',
  conditionOperations: [{
    data: {
      template: {
        putData: {
          bind: {
            theme: '{newTheme}'
          }
        }
      }
    },
    type: OperationType.data
  }, {
    data: {
      template: {
        method: 'put',
        url: '/preferences',
        bind: {
          params: '{putData}'
        }
      },
    },
    handleErrorStatuses: [ErrorCodes.FORBIDDEN, ErrorCodes.NOT_FOUND, ErrorCodes.BAD_REQUEST, ErrorCodes.INTERNAL_SERVER_ERROR],
    mappingName: 'savePreferenceResponse',
    type: OperationType.request
  }, {
    data: {
      template: {
        bind: {
          responseData: '{savePreferenceResponse}',
        }
      }
    },
    name: 'handleRequestException',
    type: OperationType.action
  }, {
    name: 'getAndPreservePreferenceData',
    type: OperationType.action
  }, {
    args: ['theme'],
    moduleName: 'Header',
    name: 'setTheme'
  }],
  elseOperations: [{
    moduleName: 'Header',
    name: 'changeThemeFailed'
  }],
  type: OperationType.conditional
}];

export const switchView: Action = [{
  moduleName: 'App',
  type: OperationType.maskModule
}, {
  data: {
    template: {
      putData: {
        bind: {
          viewType: '{newViewType}'
        }
      }
    }
  },
  type: OperationType.data
}, {
  data: {
    template: {
      method: 'put',
      url: '/preferences',
      bind: {
        params: '{putData}'
      }
    },
  },
  handleErrorStatuses: [ErrorCodes.FORBIDDEN, ErrorCodes.NOT_FOUND, ErrorCodes.BAD_REQUEST, ErrorCodes.INTERNAL_SERVER_ERROR],
  mappingName: 'responseData',
  type: OperationType.request
}, {
  name: 'handleRequestException',
  type: OperationType.action
}, {
  name: 'getAndPreservePreferenceData',
  type: OperationType.action
}, {
  data: {
    scrollPosition: 0
  },
  moduleName: 'NavigationGrid',
  args: ['scrollPosition'],
  name: 'scrollToLastPosition'
}, {
  moduleName: 'App',
  type: OperationType.unmaskModule
}];

export const handleRequestException: Action = [{
  condition: '_.get({responseData}, "response.data.errors")}',
  conditionOperations: [{
    data: {
      template: {
        bind: {
          responseData: '{responseData.response}'
        }
      }
    },
    moduleName: 'ErrorHandler',
    name: 'prepareErrorStrings',
    args: ['responseData', 'endpointPrefix'],
    mappingName: 'errorStrings',
    type: OperationType.addOperationData
  }, {
    condition: '{errorStrings.isToaster}',
    conditionOperations: [{
      data: {
        template: {
          bind: {
            toasterText: '{errorStrings.text}',
            toasterType: '{errorStrings.warningType}'
          }
        }
      },
      name: 'createToaster',
      type: OperationType.action
    }],
    elseOperations: [{
      placeholders: {
        template: {
          bind: {
            close: '{errorStrings.close}',
            text: '{errorStrings.text}',
            title: '{errorStrings.title}',
            warningClass: '{errorStrings.warningClass}'
          }
        }
      },
      items: [{
        operations: [],
        btnName: 'errorHandler.close',
        btnConfig: {
          fillMode: 'flat',
          pos: Position.end
        }
      }],
      className: '{warningClass}',
      text: 'errorHandler.text',
      title: 'errorHandler.title',
      type: OperationType.warning
    }],
    type: OperationType.conditional
  }, {
    moduleName: 'App',
    type: OperationType.unmaskModule
  }, {
    condition: '{endpointPrefix} === "DOWNLOAD_"',
    conditionOperations: [{
      moduleName: 'ToastNotifications',
      name: 'removeDownloadNotification'
    }],
    type: OperationType.conditional
  }, {
    dispatcherIds: ['mainDispatcher'],
    type: OperationType.stopAll
  }],
  type: OperationType.conditional
}];

export const unmaskModuleOnly: Action = [{
  moduleName: 'App',
  type: OperationType.unmaskModule
}]
